import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { RefreshCw, BadgeCheck, Loader2, Building2, Mail, IdCard, UserPlus, Save, Phone, CheckCircle2, MailCheck, UserX, UserCog, Combine } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { RoleBadge } from "@/components/badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { formatDateTime } from "@/lib/format";

const CHAIR_ROLES = ["Styreleder", "Nestleder", "Daglig leder"];
const ROLE_OPTIONS = ["Styreleder", "Nestleder", "Styremedlem", "Varamedlem", "Daglig leder", "Aksjonær"];
const ROLE_ORDER = { Styreleder: 0, Nestleder: 1, Styremedlem: 2, Varamedlem: 3, "Daglig leder": 4, Aksjonær: 5 };

function pickKeep(a, b) {
  const score = (r) => (r.has_user ? 2 : 0) + (r.email ? 1 : 0);
  return score(a) >= score(b) ? [a, b] : [b, a];
}

const INVITE_STATUS = {
  active: { label: "Aktiv konto", icon: CheckCircle2, cls: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" },
  invited: { label: "Invitasjon sendt", icon: MailCheck, cls: "bg-amber-500/10 text-amber-600 dark:text-amber-400" },
  none: { label: "Ingen tilgang", icon: UserX, cls: "bg-muted text-muted-foreground" },
};

function initials(n = "") {
  return n.split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
}

function InviteBadge({ status, roleId }) {
  const s = INVITE_STATUS[status] || INVITE_STATUS.none;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${s.cls}`} data-testid={`role-invite-status-${roleId}`}>
      <Icon className="h-3 w-3" /> {s.label}
    </span>
  );
}

function ContactDialog({ companyId, role, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [nid, setNid] = useState("");
  const [phone, setPhone] = useState("");

  const openDialog = () => {
    setName(role.name || "");
    setEmail(role.email || "");
    setNid("");
    setPhone(role.phone || "");
    setOpen(true);
  };

  const nidDigits = nid.replace(/\D/g, "");
  const nidInvalid = nid.length > 0 && nidDigits.length !== 11;
  const nameChanged = name.trim().length > 0 && name.trim() !== (role.name || "");
  const canSave = !nidInvalid && (nameChanged || email.trim().length > 0 || nidDigits.length === 11 || phone.trim().length > 0);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/companies/${companyId}/roles/${role.id}/contact`, {
        name: name.trim() || undefined,
        email: email.trim() || undefined,
        national_id: nidDigits || undefined,
        phone: phone.trim() || undefined,
      });
      toast.success(data.invited ? "Lagret og invitasjon sendt på e-post" : "Endringene er lagret");
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const hasContact = role.email || role.has_national_id || role.phone;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-8 gap-1.5"
        data-testid={`role-contact-btn-${role.id}`}
        onClick={openDialog}
      >
        {hasContact ? <Mail className="h-3.5 w-3.5" /> : <UserPlus className="h-3.5 w-3.5" />}
        {hasContact ? "Rediger" : "Gi tilgang"}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="role-contact-dialog">
          <DialogHeader>
            <DialogTitle>Rediger · {role.name}</DialogTitle>
            <DialogDescription>
              Endre navn eller legg til e-post, fødselsnummer og telefon slik at {role.name} får tilgang til Styrerom, kan signere med BankID
              og motta SMS-påminnelser. E-post sender automatisk en invitasjon. Fødselsnummer må være 11 siffer.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-1">
            <div>
              <Label className="text-xs">Navn</Label>
              <Input
                data-testid="role-contact-name"
                className="mt-1.5"
                placeholder="Fullt navn"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">E-post</Label>
              <Input
                data-testid="role-contact-email"
                type="email"
                className="mt-1.5"
                placeholder="navn@example.no"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-xs">Fødselsnummer (11 siffer)</Label>
              <Input
                data-testid="role-contact-nid"
                inputMode="numeric"
                className="mt-1.5"
                placeholder="11 siffer"
                value={nid}
                onChange={(e) => setNid(e.target.value)}
              />
              {role.has_national_id && !nid ? (
                <p className="mt-1 text-[11px] text-emerald-600 dark:text-emerald-400" data-testid="role-contact-nid-registered">
                  Fødselsnummer er allerede registrert. La feltet stå tomt for å beholde det.
                </p>
              ) : null}
              {nidInvalid ? (
                <p className="mt-1 text-[11px] text-destructive" data-testid="role-contact-nid-error">
                  Fødselsnummer må være nøyaktig 11 siffer.
                </p>
              ) : null}
            </div>
            <div>
              <Label className="text-xs">Telefon (valgfritt · for SMS-påminnelser)</Label>
              <Input
                data-testid="role-contact-phone"
                type="tel"
                inputMode="tel"
                className="mt-1.5"
                placeholder="+47 400 00 000"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Avbryt</Button>
            <Button className="gap-2" data-testid="role-contact-save" onClick={save} disabled={saving || !canSave}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Lagre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RoleChangeDialog({ companyId, role, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [value, setValue] = useState(role.role);
  const options = role.is_company ? ["Aksjonær"] : ROLE_OPTIONS;

  const openDialog = () => {
    setValue(role.role);
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/companies/${companyId}/roles/${role.id}/role`, { role: value });
      toast.success(`Rolle endret til ${value}`);
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-8 gap-1.5"
        data-testid={`role-change-btn-${role.id}`}
        onClick={openDialog}
      >
        <UserCog className="h-3.5 w-3.5" /> Endre rolle
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="role-change-dialog">
          <DialogHeader>
            <DialogTitle>Endre rolle · {role.name}</DialogTitle>
            <DialogDescription>
              {role.is_company
                ? "Et selskap kan kun være aksjonær/eier – ikke styremedlem, styreleder eller daglig leder."
                : `Velg riktig rolle for ${role.name}. Endringen påvirker tilganger og hvordan personen vises i styrerommet.`}
            </DialogDescription>
          </DialogHeader>

          <div className="py-1">
            <Label className="text-xs">Rolle</Label>
            <Select value={value} onValueChange={setValue} disabled={role.is_company}>
              <SelectTrigger className="mt-1.5" data-testid="role-change-select">
                <SelectValue placeholder="Velg rolle" />
              </SelectTrigger>
              <SelectContent>
                {options.map((r) => (
                  <SelectItem key={r} value={r} data-testid={`role-change-option-${r}`}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Avbryt</Button>
            <Button className="gap-2" data-testid="role-change-save" onClick={save} disabled={saving || value === role.role}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Lagre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function MergeDialog({ companyId, base, preselect, roles, onClose, onSaved }) {
  const [partnerId, setPartnerId] = useState(preselect?.id || "");
  const [saving, setSaving] = useState(false);
  const others = roles.filter((r) => r.id !== base.id);
  const partner = others.find((r) => r.id === partnerId) || null;
  const [keep, remove] = partner ? pickKeep(base, partner) : [null, null];
  const resultRole = keep && remove
    ? [keep.role, remove.role].sort((x, y) => (ROLE_ORDER[x] ?? 9) - (ROLE_ORDER[y] ?? 9))[0]
    : null;

  const doMerge = async () => {
    if (!keep || !remove) return;
    setSaving(true);
    try {
      await api.post(`/companies/${companyId}/roles/merge`, { keep_id: keep.id, remove_id: remove.id });
      toast.success("Rollene er slått sammen");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="merge-roles-dialog">
        <DialogHeader>
          <DialogTitle>Slå sammen roller</DialogTitle>
          <DialogDescription>
            Slå sammen to oppføringer som er samme person. Brukerkonto/kontaktinfo og den mest seniore rollen beholdes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-1">
          <div className="rounded-lg border border-border/60 p-3">
            <p className="text-sm font-medium">{base.name}</p>
            <p className="text-xs text-muted-foreground">{base.role} · {base.has_user ? "Har konto" : "Uten konto (Proff)"}</p>
          </div>

          {!partner ? (
            <>
              <Label className="text-xs">Slå sammen med</Label>
              <div className="max-h-56 space-y-1.5 overflow-y-auto">
                {others.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Ingen andre roller å slå sammen med.</p>
                ) : others.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    data-testid={`merge-partner-${r.id}`}
                    onClick={() => setPartnerId(r.id)}
                    className="flex w-full items-center justify-between gap-2 rounded-lg border border-border/60 px-3 py-2 text-left transition-colors hover:bg-muted"
                  >
                    <span className="truncate text-sm">{r.name}</span>
                    <span className="shrink-0 text-[11px] text-muted-foreground">{r.role} · {r.has_user ? "Har konto" : "Proff"}</span>
                  </button>
                ))}
              </div>
            </>
          ) : (
            <div className="rounded-lg border border-primary/40 bg-primary/5 p-3">
              <p className="text-xs text-muted-foreground">Resultat etter sammenslåing:</p>
              <p className="mt-1 text-sm font-medium" data-testid="merge-result-name">{keep.name}</p>
              <p className="text-xs text-muted-foreground">Rolle: {resultRole} · beholder {keep.has_user ? "kontoen" : "kontaktinfo"}</p>
              <button type="button" className="mt-2 text-xs text-primary hover:underline" data-testid="merge-change-partner" onClick={() => setPartnerId("")}>Velg en annen</button>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Avbryt</Button>
          {partner ? (
            <Button className="gap-2" data-testid="merge-confirm" onClick={doMerge} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Combine className="h-4 w-4" />} Slå sammen
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function Roles() {
  const { companyId } = useParams();
  const { company, refetchCompany } = useOutletContext();
  const { user } = useAuth();
  const [roles, setRoles] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [mergeState, setMergeState] = useState(null);

  const canManage = !!user?.is_admin || (company?.my_roles || []).some((r) => CHAIR_ROLES.includes(r));

  const dupGroups = useMemo(() => {
    if (!roles) return [];
    const map = {};
    roles.forEach((r) => { if (r.dup_key) (map[r.dup_key] = map[r.dup_key] || []).push(r); });
    return Object.values(map).filter((g) => g.length > 1);
  }, [roles]);

  const load = useCallback(() => api.get(`/companies/${companyId}/roles`).then((r) => setRoles(r.data)), [companyId]);

  useEffect(() => {
    setRoles(null);
    load();
  }, [load]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/sync-proff`);
      toast.success(data.message);
      await Promise.all([load(), refetchCompany?.()]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Roller" description="Roller hentes automatisk fra Brønnøysundregisteret og Proff. Legg til e-post og fødselsnummer for å gi styremedlemmer tilgang til Styrerom.">
        <Button variant="outline" onClick={sync} disabled={syncing} className="gap-2" data-testid="sync-proff-btn">
          {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Synk fra Proff
        </Button>
      </PageHeader>

      <div className="mb-5 flex items-center gap-3 rounded-xl border border-border/60 bg-card p-4 shadow-sm">
        <div className="grid h-10 w-10 place-items-center rounded-lg bg-primary/10 text-primary"><Building2 className="h-5 w-5" strokeWidth={1.7} /></div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">{company?.name}</p>
          <p className="text-xs text-muted-foreground">Sist synkronisert: {formatDateTime(company?.proff_synced_at)}</p>
        </div>
        <BadgeCheck className="h-5 w-5 text-emerald-500" />
      </div>

      {canManage && dupGroups.length > 0 ? (
        <div className="mb-5 space-y-2" data-testid="merge-suggestions">
          {dupGroups.map((g) => (
            <div key={g[0].dup_key} className="flex items-center gap-3 rounded-xl border border-amber-500/40 bg-amber-500/5 p-3">
              <Combine className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <p className="flex-1 text-sm"><span className="font-medium">{g[0].name}</span> finnes i {g.length} oppføringer – samme person?</p>
              <Button size="sm" data-testid={`merge-suggest-btn-${g[0].dup_key}`} onClick={() => setMergeState({ base: g[0], preselect: g[1] })}>Slå sammen</Button>
            </div>
          ))}
        </div>
      ) : null}

      <div className="space-y-3">
        {roles === null
          ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)
          : roles.map((r, i) => (
              <motion.div key={r.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} data-testid={`role-row-${r.id}`} className="flex items-center gap-4 rounded-xl border border-border/60 bg-card p-4 shadow-sm">
                <Avatar className="h-10 w-10"><AvatarFallback className="bg-primary/10 text-sm font-semibold text-primary">{initials(r.name)}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate font-medium">{r.name}</p>
                    {r.is_owner ? <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-600 dark:text-amber-400">Eier</span> : null}
                    <InviteBadge status={r.invite_status} roleId={r.id} />
                  </div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    <span className="truncate">{r.email || "Ingen e-post"}</span>
                    {r.phone ? (
                      <span className="inline-flex items-center gap-1" data-testid={`role-phone-${r.id}`}><Phone className="h-3 w-3" /> {r.phone}</span>
                    ) : null}
                    {r.has_national_id ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400" data-testid={`role-nid-badge-${r.id}`}>
                        <IdCard className="h-3 w-3" /> Fødselsnummer registrert
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <RoleBadge role={r.role} />
                  {canManage ? (
                    <div className="flex items-center gap-1.5">
                      <RoleChangeDialog companyId={companyId} role={r} onSaved={load} />
                      <ContactDialog companyId={companyId} role={r} onSaved={load} />
                      {roles.length > 1 ? (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" title="Slå sammen med en annen rolle" data-testid={`role-merge-btn-${r.id}`} onClick={() => setMergeState({ base: r })}>
                          <Combine className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </motion.div>
            ))}
      </div>

      {mergeState ? (
        <MergeDialog
          companyId={companyId}
          base={mergeState.base}
          preselect={mergeState.preselect}
          roles={roles || []}
          onClose={() => setMergeState(null)}
          onSaved={load}
        />
      ) : null}
    </div>
  );
}
